import { keyframes } from "@mui/material";
import { alpha, SxProps, Theme } from "@mui/material/styles";
import { compareColors as c, compareTokens as t, typeTone } from "./ContractCompare.tokens";
import { CompareChangeType } from "./ContractCompareTypesAndValidation";

type Sx = SxProps<Theme>;

/** Keeps literals such as `flexDirection: "column"` typed for `sx` without a cast per entry (CLAUDE.md §13). */
const defineStyles = <T extends Record<string, Sx | ((...args: never[]) => Sx)>>(styles: T): T => styles;

const sideBySide = t.sideBySideFrom;
const phone = t.sheetBelow;
/** A phone held sideways (740x360 in the frames): wider than `sm`, but still a phone, so the comparing panel's Cancel foot sticks and fills. */
const landscapePhone = "@media (orientation: landscape) and (max-height: 500px)";

const slide = keyframes`
  0% { transform: translateX(-110%); }
  100% { transform: translateX(480%); }
`;
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const quoteBase = {
  boxSizing: "border-box",
  m: 0,
  px: t.result.card.quote.paddingX,
  py: t.result.card.quote.paddingY,
  background: c.fill,
  borderLeft: `${t.result.card.quote.accent} solid ${c.stepRing}`,
  borderRadius: `0 ${t.result.card.quote.radius} ${t.result.card.quote.radius} 0`,
  color: c.body,
  fontSize: t.result.card.quote.size,
  lineHeight: t.result.card.quote.line,
  overflowWrap: "anywhere",
  whiteSpace: "pre-wrap",
} as const;

const quoteClamp = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: t.result.card.quote.collapsedLines,
  overflow: "hidden",
} as const;

export const compareStyles = defineStyles({
  /** The content column: heading row, then one of the page's views. */
  page: {
    display: "flex",
    flexDirection: "column",
    gap: t.page.gap,
    width: "100%",
    minWidth: 0,
  },

  // ------------------------------------------------------------------------------------------ heading
  /** The page header: the title row (the title, and Compare on its right in the picker), then the subtitle. */
  header: {
    display: "flex",
    flexDirection: "column",
    pb: "4px",
  },
  /**
   * Beside Compare the title wraps down to `t.page.titleMin` (two lines at 360); if the two still do not fit one row
   * (a very narrow phone, a long title, or the "Compare · 60" label) the button drops under the title, still on the
   * right. The DOM order is the reading order: title, Compare, subtitle, steps.
   */
  titleRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    columnGap: "16px",
    rowGap: "12px",
  },
  /** The result's header: the text, and "Compare other files" on its right. */
  headerText: { minWidth: 0, flex: "1 1 320px" },
  title: {
    m: 0,
    flex: `1 1 ${t.page.titleMin}`,
    minWidth: 0,
    color: c.ink,
    fontSize: { xs: t.page.titlePhone.size, [sideBySide]: t.page.title.size },
    lineHeight: { xs: t.page.titlePhone.line, [sideBySide]: t.page.title.line },
    fontWeight: t.page.title.weight,
    letterSpacing: t.page.title.tracking,
    "&:focus": { outline: "none" },
  },
  resultTitle: {
    m: 0,
    color: c.ink,
    fontSize: t.result.titleSize,
    fontWeight: 800,
    lineHeight: 1.25,
    "&:focus": { outline: "none" },
  },
  subtitle: {
    m: 0,
    pt: "6px",
    color: c.sub,
    fontSize: t.page.subtitle.size,
    lineHeight: t.page.subtitle.line,
  },
  summary: {
    m: 0,
    pt: "6px",
    color: c.body,
    fontSize: t.result.summarySize,
    fontWeight: 500,
    lineHeight: 1.4,
  },

  // ------------------------------------------------------------------------------------------ steps
  steps: {
    display: "grid",
    gridTemplateColumns: { xs: "minmax(0, 1fr)", [sideBySide]: "repeat(2, minmax(0, 1fr))" },
    gap: { xs: "16px", [sideBySide]: t.step.columnGap },
    alignItems: "start",
  },
  stepCard: {
    display: "flex",
    flexDirection: "column",
    gap: t.step.gap,
    minWidth: 0,
    boxSizing: "border-box",
    p: { xs: t.step.paddingPhone, [phone]: t.step.padding },
    background: c.white,
    border: `1px solid ${c.edge}`,
    borderRadius: t.step.radius,
  },
  stepHead: { display: "flex", alignItems: "flex-start", gap: "12px" },
  stepBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: t.step.badge,
    height: t.step.badge,
    borderRadius: t.step.radius,
    background: c.ink,
    color: c.white,
    fontSize: t.step.badgeFont,
    fontWeight: 700,
  },
  stepTitle: { m: 0, color: c.ink, fontSize: t.step.titleSize, fontWeight: 800, lineHeight: 1.25 },
  stepHint: { m: 0, pt: "2px", color: c.sub, fontSize: t.step.hintSize },

  tabs: {
    display: "flex",
    gap: t.tabs.gap,
    borderBottom: `1px solid ${c.edge}`,
    overflowX: "auto",
  },
  /**
   * One tab. From `sm` it is as wide as its icon and label, in a strip that scrolls if it must. On a phone (the 360
   * frame) the two tabs share the strip, with the frame's 6 px of side padding and a 6 px icon gap, so both show whole
   * at 360 without scrolling; below that, or with larger text, the label wraps onto a second line (the tab keeps its
   * 44 px) instead of the strip scrolling and cutting the first tab off.
   */
  tab: (active: boolean): Sx => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: { xs: "center", [phone]: "flex-start" },
    gap: { xs: t.tabs.iconGapPhone, [phone]: t.tabs.iconGap },
    height: t.tabs.height,
    boxSizing: "border-box",
    px: { xs: t.tabs.paddingXPhone, [phone]: t.tabs.paddingX },
    mb: "-1px",
    flex: { xs: "1 1 auto", [phone]: "0 0 auto" },
    "& svg": { flexShrink: 0 },
    background: "transparent",
    border: 0,
    borderBottom: `2px solid ${active ? c.brand : "transparent"}`,
    color: active ? c.brand : c.sub,
    fontSize: t.tabs.size,
    fontWeight: active ? 700 : 600,
    cursor: "pointer",
    "&:hover": { color: active ? c.brand : c.ink },
  }),

  searchBox: (focused: boolean): Sx => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    height: t.search.height,
    boxSizing: "border-box",
    px: t.search.paddingX,
    border: `1px solid ${c.edge}`,
    borderRadius: t.search.radius,
    background: c.white,
    color: c.faint,
    // Set from the input's own focus: `:focus-within` is not used (jsdom's selector engine cannot resolve it).
    ...(focused ? { borderColor: c.brand } : {}),
  }),
  searchInput: {
    flex: 1,
    minWidth: 0,
    border: 0,
    outline: "none",
    background: "transparent",
    color: c.ink,
    fontFamily: "inherit",
    fontSize: "14px",
    "&::placeholder": { color: c.faint, opacity: 1 },
  },
  iconButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: { xs: t.touchTarget, [phone]: t.search.clear },
    height: { xs: t.touchTarget, [phone]: t.search.clear },
    // On a phone the 44 px target is as tall as the search box itself: it reaches the box's 1 px edge, not past it.
    my: { xs: "-1px", [phone]: 0 },
    mr: { xs: "-12px", [phone]: 0 },
    p: 0,
    border: 0,
    borderRadius: "8px",
    background: "transparent",
    color: c.sub,
    cursor: "pointer",
    "&:hover": { background: c.chipFill },
  },
  hint: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    m: 0,
    color: c.sub,
    fontSize: t.hint.size,
    lineHeight: t.hint.line,
  },

  // ------------------------------------------------------------------------------------------ library list
  list: {
    m: 0,
    p: 0,
    listStyle: "none",
    height: { xs: "auto", [phone]: t.list.height },
    maxHeight: { xs: "none", [phone]: t.list.height },
    overflowY: "auto",
    border: `1px solid ${c.edge}`,
    borderRadius: t.list.radius,
    background: c.white,
  },
  listInSheet: {
    m: 0,
    p: 0,
    listStyle: "none",
    flex: 1,
    overflowY: "auto",
  },
  row: (selected: boolean, last = false): Sx => ({
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: t.list.rowGap,
    width: "100%",
    minHeight: t.list.rowMinHeight,
    boxSizing: "border-box",
    px: t.list.rowPaddingX,
    py: t.list.rowPaddingY,
    textAlign: "left",
    fontFamily: "inherit",
    border: 0,
    borderBottom: last ? 0 : `1px solid ${c.hairline}`,
    background: selected ? c.tint : c.white,
    boxShadow: selected ? `inset ${t.list.selectedAccent} 0 0 0 ${c.brand}` : "none",
    cursor: "pointer",
    "&:hover": { background: selected ? c.tint : c.fill },
    // The native radio inside is visually hidden: a keyboard focus on it draws the ring on its sibling below.
    "& > input:focus-visible ~ .compare-row-ring": { outline: `2px solid ${c.focus}`, outlineOffset: "-2px" },
  }),
  rowRing: { position: "absolute", inset: 0, pointerEvents: "none", borderRadius: "inherit" },
  radio: (selected: boolean): Sx => ({
    flexShrink: 0,
    boxSizing: "border-box",
    width: t.list.radio,
    height: t.list.radio,
    borderRadius: "50%",
    background: c.white,
    border: selected ? `${t.list.radioChecked} solid ${c.brand}` : `2px solid ${c.ring}`,
  }),
  rowText: { display: "flex", flexDirection: "column", gap: "3px", minWidth: 0, flex: 1 },
  fileName: {
    color: c.ink,
    fontSize: t.list.nameSize,
    fontWeight: 600,
    lineHeight: t.list.nameLine,
    overflowWrap: "anywhere",
  },
  fileMeta: { color: c.sub, fontSize: t.list.metaSize, lineHeight: 1.3 },

  kind: (docx: boolean, small = false): Sx => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "1px",
    flexShrink: 0,
    width: small ? t.kind.small.width : t.kind.width,
    height: small ? t.kind.small.height : t.kind.height,
    borderRadius: t.kind.radius,
    background: docx ? c.docxFill : c.dangerFill,
    color: docx ? c.docx : c.danger,
    fontSize: small ? t.kind.small.label : t.kind.label,
    fontWeight: 800,
    letterSpacing: "0.03em",
    lineHeight: 1.2,
  }),

  emptyList: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    textAlign: "center",
    minHeight: 200,
    p: "24px",
    border: `1px solid ${c.edge}`,
    borderRadius: t.list.radius,
    color: c.sub,
    fontSize: "14px",
    lineHeight: 1.55,
  },

  // ------------------------------------------------------------------------------------------ picked banner (flow B)
  pickedBanner: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
    boxSizing: "border-box",
    p: "12px 14px",
    border: `1px solid ${c.tintEdge}`,
    borderRadius: t.chip.radius,
    background: c.tint,
  },
  eyebrow: {
    display: "block",
    color: c.sub,
    fontSize: t.result.strip.eyebrow,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },

  // ------------------------------------------------------------------------------------------ file chip + upload
  chip: (invalid: boolean): Sx => ({
    display: "flex",
    alignItems: "center",
    gap: t.chip.gap,
    boxSizing: "border-box",
    width: "100%",
    pl: t.chip.paddingLeft,
    pr: t.chip.paddingRight,
    py: t.chip.paddingY,
    background: invalid ? t.chip.invalidFill : c.white,
    border: `1px solid ${invalid ? c.tintEdge : c.edge}`,
    borderRadius: t.chip.radius,
  }),
  chipStatus: (invalid: boolean): Sx => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    color: invalid ? c.danger : c.success,
    fontSize: "12px",
    fontWeight: 700,
  }),
  chipRemove: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: t.chip.remove,
    height: t.chip.remove,
    p: 0,
    border: 0,
    borderRadius: t.chip.removeRadius,
    background: "transparent",
    color: c.body,
    cursor: "pointer",
    "&:hover": { background: c.chipFill },
  },
  fieldError: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    m: 0,
    color: c.danger,
    fontSize: "13px",
    fontWeight: 700,
    lineHeight: 1.45,
  },
  dropBody: (compact: boolean): Sx => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: t.dropzone.gap,
    boxSizing: "border-box",
    width: "100%",
    minHeight: compact ? t.dropzone.minHeightCompact : t.dropzone.minHeight,
    px: t.dropzone.paddingX,
    py: compact ? "28px" : t.dropzone.paddingY,
    textAlign: "center",
    cursor: "pointer",
    background: c.fill,
    // The dropzone merges this over its own 2px / neutral.300 dashed border, so every border part is stated.
    borderWidth: "1px",
    borderStyle: "dashed",
    borderColor: c.dashed,
    borderRadius: t.dropzone.radius,
    "&:hover, &:focus-visible": { borderColor: c.brand },
  }),
  dropOver: { borderColor: c.brand, backgroundColor: c.tint },
  dropIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: t.dropzone.iconTile,
    height: t.dropzone.iconTile,
    borderRadius: "50%",
    background: c.tint,
    color: c.brand,
  },
  dropTitle: { m: 0, color: c.ink, fontSize: t.dropzone.titleSize, fontWeight: 700 },
  dropMeta: { m: 0, color: c.sub, fontSize: t.dropzone.metaSize },
  browse: { color: c.brandText, fontWeight: 700, textDecoration: "underline" },

  // ------------------------------------------------------------------------------------------ compare button
  /**
   * Compare, in the page header at every size (Jay, 2026-11-04): on the right of the title, or, where the two do not
   * fit one row, under it, still on the right. It scrolls with the page; nothing is fixed to the screen. Layout only:
   * the button's look is the theme's (`size="large"`, 44 px, section 11.4).
   */
  compareButton: {
    ml: "auto",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  /** A secondary button of the page: the frames' height from `sm`, a 44 px target on a phone. */
  actionButton: { minHeight: { xs: t.touchTarget, [phone]: t.button.height } },
  /** Cancel, alone in the progress foot: on the right from `sm` (the frames), the foot's whole width on a phone. */
  cancelButton: {
    minHeight: { xs: t.touchTarget, [phone]: t.button.height },
    width: { xs: "100%", [phone]: "auto" },
    [landscapePhone]: { width: "100%" },
  },
  /** "Change", a small button in the frames (32 px), a 44 px target on a phone. */
  changeButton: { minHeight: { xs: t.touchTarget, [phone]: t.button.small } },
  /** The busy or refusal line directly under the header: warning for a busy answer, danger for a refusal that names no file. */
  compareMessage: (tone: "warning" | "danger"): Sx => ({
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    m: 0,
    p: "10px 14px",
    borderRadius: t.compare.messageRadius,
    background: tone === "danger" ? c.dangerFill : c.warningFill,
    color: tone === "danger" ? c.danger : c.warning,
    fontSize: "13.5px",
    fontWeight: 700,
    lineHeight: 1.45,
  }),

  // ------------------------------------------------------------------------------------------ panels
  panelWrap: { display: "flex", justifyContent: "center", pt: "12px", width: "100%" },
  panel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: t.panel.gap,
    boxSizing: "border-box",
    width: "100%",
    maxWidth: t.panel.width,
    px: { xs: "20px", [phone]: t.panel.paddingX },
    py: { xs: "32px", [phone]: t.panel.paddingY },
    textAlign: "center",
    background: c.white,
    border: `1px solid ${c.edge}`,
    borderRadius: t.panel.radius,
  },
  panelIcon: (fill: string, color: string): Sx => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: t.panel.iconTile,
    height: t.panel.iconTile,
    borderRadius: "50%",
    background: fill,
    color,
  }),
  panelTitle: (color: string, align: "center" | "left" = "center"): Sx => ({
    m: 0,
    textAlign: align,
    color,
    fontSize: t.panel.titleSize,
    fontWeight: 800,
    lineHeight: t.panel.titleLine,
    "&:focus": { outline: "none" },
  }),
  panelText: (bold: boolean): Sx => ({
    m: 0,
    maxWidth: t.panel.textWidth,
    color: c.sub,
    fontSize: t.panel.textSize,
    fontWeight: bold ? 700 : 400,
    lineHeight: t.panel.textLine,
  }),
  panelActions: { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "12px", pt: "8px" },

  // ------------------------------------------------------------------------------------------ progress
  progressPanel: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    boxSizing: "border-box",
    width: "100%",
    maxWidth: t.panel.width,
    p: { xs: "20px", [phone]: t.panel.progressPadding },
    background: c.white,
    border: `1px solid ${c.edge}`,
    borderRadius: t.panel.radius,
    boxShadow: t.panel.shadow,
  },
  progressHead: { display: "flex", alignItems: "center", gap: "16px" },
  progressIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: t.panel.iconTileProgress,
    height: t.panel.iconTileProgress,
    borderRadius: "12px",
    background: c.tint,
    color: c.brand,
  },
  progressNames: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "4px 8px",
    pt: "4px",
    color: c.sub,
    fontSize: "13px",
    overflowWrap: "anywhere",
  },
  steps2: { display: "flex", flexDirection: "column", gap: "6px", m: 0, p: 0, listStyle: "none" },
  step: (active: boolean): Sx => ({
    display: "flex",
    alignItems: "flex-start",
    gap: t.panel.stepGap,
    boxSizing: "border-box",
    px: t.panel.stepPaddingX,
    py: t.panel.stepPaddingY,
    borderRadius: t.panel.stepRadius,
    background: active ? c.fill : "transparent",
    border: active ? `1px solid ${c.edge}` : "1px solid transparent",
  }),
  stepMark: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxSizing: "border-box",
    width: t.panel.stepHeight,
    height: t.panel.stepHeight,
    borderRadius: "50%",
    border: `2px solid ${c.stepRing}`,
    color: c.faint,
    fontSize: "13px",
    fontWeight: 700,
  },
  spinner: {
    flexShrink: 0,
    boxSizing: "border-box",
    width: t.panel.stepHeight,
    height: t.panel.stepHeight,
    borderRadius: "50%",
    border: `3px solid ${c.tintEdge}`,
    borderTopColor: c.brand,
    animation: `${spin} 0.9s linear infinite`,
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
  stepBody: { display: "flex", flexDirection: "column", gap: "10px", flex: 1, minWidth: 0 },
  stepLine: { display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: t.panel.stepHeight, gap: "12px" },
  stepName: (active: boolean): Sx => ({
    color: active ? c.ink : c.faint,
    fontSize: "15px",
    fontWeight: active ? 600 : 500,
  }),
  stepState: { color: c.brandText, fontSize: "12px", fontWeight: 700, flexShrink: 0 },
  track: {
    position: "relative",
    overflow: "hidden",
    height: t.panel.barHeight,
    borderRadius: t.panel.barRadius,
    background: c.hairline,
  },
  trackFill: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "40%",
    height: "100%",
    borderRadius: t.panel.barRadius,
    background: `linear-gradient(90deg, ${alpha(c.brand, 0.15)}, ${c.brand}, ${alpha(c.brand, 0.15)})`,
    animation: `${slide} 1.8s ease-in-out infinite`,
    "@media (prefers-reduced-motion: reduce)": { animation: "none", width: "100%", opacity: 0.5 },
  },
  progressFoot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: "12px",
    pt: "18px",
    borderTop: `1px solid ${c.hairline}`,
    // Phone (also landscape): Cancel stays reachable in a bar fixed to the bottom of the panel's view.
    position: { xs: "sticky", [phone]: "static" },
    bottom: { xs: 0, [phone]: "auto" },
    background: c.white,
    pb: { xs: "12px", [phone]: 0 },
    [landscapePhone]: { position: "sticky", bottom: 0 },
  },
  stillNote: { m: 0, px: "14px", pt: "6px", color: c.sub, fontSize: "13px" },

  // ------------------------------------------------------------------------------------------ result
  resultHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap",
  },
  strip: {
    display: "flex",
    alignItems: { xs: "stretch", [phone]: "center" },
    gap: t.result.strip.gap,
    boxSizing: "border-box",
    px: t.result.strip.paddingX,
    py: t.result.strip.paddingY,
    background: c.fill,
    border: `1px solid ${c.edge}`,
    borderRadius: t.result.strip.radius,
    flexDirection: { xs: "column", [phone]: "row" },
  },
  stripSide: { display: "flex", flexDirection: "column", gap: "4px", flex: 1, minWidth: 0, width: "100%" },
  stripName: { display: "flex", alignItems: "center", gap: "8px", minWidth: 0 },
  stripFile: { color: c.ink, fontSize: "14px", fontWeight: 700, overflowWrap: "anywhere" },
  stripArrow: { display: "flex", color: c.sub, flexShrink: 0, transform: { xs: "rotate(90deg)", [phone]: "none" } },
  notices: { display: "flex", flexDirection: "column", gap: "8px", m: 0, p: 0, listStyle: "none" },
  notice: (capped: boolean): Sx => ({
    display: "flex",
    alignItems: "flex-start",
    gap: t.result.notice.gap,
    boxSizing: "border-box",
    px: t.result.notice.paddingX,
    py: t.result.notice.paddingY,
    borderRadius: t.result.notice.radius,
    border: `1px solid ${capped ? c.warningEdge : c.edge}`,
    background: capped ? c.warningFill : c.fill,
    color: capped ? c.warning : c.body,
    fontSize: t.result.notice.size,
    lineHeight: 1.5,
  }),
  filters: {
    display: "flex",
    gap: t.result.chip.gap,
    overflowX: "auto",
    pb: "4px",
    m: 0,
    p: 0,
    listStyle: "none",
  },
  filterChip: (active: boolean): Sx => ({
    display: "inline-flex",
    alignItems: "center",
    gap: t.result.chip.gap,
    flexShrink: 0,
    minHeight: { xs: t.touchTarget, [phone]: t.result.chip.height },
    boxSizing: "border-box",
    px: t.result.chip.paddingX,
    borderRadius: t.result.chip.radius,
    border: `1px solid ${active ? c.ink : c.edge}`,
    background: active ? c.ink : c.white,
    color: active ? c.white : c.body,
    fontFamily: "inherit",
    fontSize: t.result.chip.size,
    fontWeight: 600,
    cursor: "pointer",
    "&:focus-visible": { outline: `2px solid ${c.focus}`, outlineOffset: "2px" },
  }),
  filterCount: (active: boolean): Sx => ({
    px: "8px",
    py: "2px",
    borderRadius: "10px",
    background: active ? alpha(c.white, 0.18) : c.chipFill,
    color: active ? c.white : c.ink,
    fontSize: t.result.chip.countSize,
    fontWeight: 700,
  }),
  legend: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px 18px",
    m: 0,
    color: c.sub,
    fontSize: t.result.legend.size,
  },
  struck: {
    px: "2px",
    borderRadius: "3px",
    background: c.dangerFill,
    color: c.danger,
    fontWeight: 600,
    textDecoration: "line-through",
  },
  underlined: {
    px: "2px",
    borderRadius: "3px",
    background: c.successFill,
    color: c.success,
    fontWeight: 600,
    textDecoration: "underline",
  },
  cards: { display: "flex", flexDirection: "column", gap: t.result.card.listGap, m: 0, p: 0, listStyle: "none" },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: t.result.card.gap,
    boxSizing: "border-box",
    minWidth: 0,
    pt: t.result.card.paddingTop,
    pb: t.result.card.paddingBottom,
    px: { xs: "16px", [phone]: t.result.card.paddingX },
    background: c.white,
    border: `1px solid ${c.edge}`,
    borderRadius: t.result.card.radius,
  },
  cardTop: {
    display: "flex",
    alignItems: "center",
    flexWrap: { xs: "wrap", md: "nowrap" },
    gap: "12px",
  },
  tag: (type: CompareChangeType | "other"): Sx => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    flexShrink: 0,
    height: t.result.card.tag.height,
    boxSizing: "border-box",
    px: t.result.card.tag.paddingX,
    borderRadius: t.result.card.tag.radius,
    background: typeTone[type].fill,
    color: typeTone[type].color,
    fontSize: t.result.card.tag.size,
    fontWeight: 700,
  }),
  cardHeading: {
    m: 0,
    flex: 1,
    minWidth: 0,
    color: c.ink,
    fontSize: t.result.card.heading,
    fontWeight: 700,
    overflowWrap: "anywhere",
    order: { xs: 3, md: 0 },
    flexBasis: { xs: "100%", md: "auto" },
  },
  location: {
    ml: "auto",
    px: "8px",
    py: "4px",
    borderRadius: t.result.card.location.radius,
    background: c.locationFill,
    color: c.sub,
    fontSize: t.result.card.location.size,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  sentenceRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px 12px" },
  sentence: {
    m: 0,
    flex: "1 1 240px",
    color: c.ink,
    fontSize: t.result.card.sentence.size,
    fontWeight: 600,
    lineHeight: t.result.card.sentence.line,
    overflowWrap: "anywhere",
  },
  codeTag: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    flexShrink: 0,
    px: "8px",
    py: "3px",
    border: `1px dashed ${c.codeTagEdge}`,
    borderRadius: t.result.card.codeTag.radius,
    color: c.body,
    fontSize: t.result.card.codeTag.size,
    fontWeight: 700,
  },
  /** Collapsed after six lines unless expanded. */
  quote: (expanded: boolean): Sx => (expanded ? quoteBase : { ...quoteBase, ...quoteClamp }),
  del: { color: c.danger, textDecoration: "line-through" },
  ins: { color: c.success, textDecoration: "underline" },
  // Pixels as strings: in `sx` a number up to 1 is a fraction of the parent, so `width: 1` is 100 %, not 1 px, and
  // every screen-reader label became as wide as the screen (the page scrolled sideways).
  srOnly: {
    position: "absolute",
    width: "1px",
    height: "1px",
    p: 0,
    m: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    border: 0,
  },
  quoteNote: { m: 0, color: c.sub, fontSize: "12px", fontStyle: "italic" },
  moreRow: { display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: "12px", pt: "4px", color: c.sub, fontSize: "13px" },
  linkButton: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    minHeight: { xs: t.touchTarget, [phone]: 0 },
    p: "4px 0",
    border: 0,
    background: "transparent",
    color: c.brand,
    fontFamily: "inherit",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
    "&:hover": { textDecoration: "underline" },
  },

  // ------------------------------------------------------------------------------------------ phone sheet
  sheetBody: { display: "flex", flexDirection: "column", gap: "12px", flex: 1, minHeight: 0, p: "16px" },
});

export default compareStyles;
